import React from 'react';
import { usePortal } from '../../contexts/PortalContext';
import type { Deliverable, Project } from '../../types/portal';
import { waitingLabel } from '../../utils/deliverables';
interface Row {
  key: string;
  projectId: string;
  deliverableId: string;
  /** Priority: lower is more actionable. */
  rank: number;
  /** Epoch ms the item started waiting, so the oldest can rise to the top. */
  since?: number;
  /** e.g. "Smart Cities approved" */
  headline: string;
  /** e.g. "BLOOMBERG · V3 · APPROVED BY MORGAN" */
  detail: string;
  /** e.g. "Ready for Portal to publish" */
  next: string;
}
function detailLine(project: Project, parts: (string | undefined)[]): string {
  return [project.client, ...parts].filter(Boolean).join(' · ');
}

/**
 * What the studio has to act on. Each line names the deliverable, the client,
 * its current state and Portal's next move — derived from live state, with the
 * longest-waiting work first inside each kind of action.
 */
export function NeedsAttention() {
  const {
    activeProjects,
    openActivity
  } = usePortal();
  const rows: Row[] = [];
  const push = (project: Project, item: Deliverable, row: Omit<Row, 'key' | 'projectId' | 'deliverableId'>) => rows.push({
    ...row,
    key: `${item.id}-${row.rank}`,
    projectId: project.id,
    deliverableId: item.id
  });
  for (const project of activeProjects) {
    for (const item of project.deliverables) {
      /* Only genuine shared records count — prototype seed data never does. */
      const outstanding = item.changeRequests.filter(request => !request.resolved && request.shared);
      if (outstanding.length > 0) {
        /* The oldest unresolved request is what the waiting time refers to. */
        const oldest = outstanding.reduce<number | undefined>((earliest, request) => request.at && (!earliest || request.at < earliest) ? request.at : earliest, undefined);
        push(project, item, {
          rank: 1,
          since: oldest,
          headline: `${item.name} — changes requested`,
          detail: detailLine(project, [item.version, `${outstanding.length} outstanding change${outstanding.length === 1 ? '' : 's'}`]),
          next: 'Action the changes, then publish a new version'
        });
        continue;
      }

      /* An approval only lands here when a real client actually gave it. */
      if (item.status === 'Approved' && item.approvedBy) {
        push(project, item, {
          rank: 2,
          since: item.approvedAt,
          headline: `${item.name} approved`,
          detail: detailLine(project, [item.version, `Approved by ${item.approvedBy}`]),
          next: 'Ready for Portal to publish the final files'
        });
      }
    }
  }

  /* Most actionable first, then longest waiting within each kind. */
  rows.sort((a, b) => a.rank - b.rank || (a.since ?? Infinity) - (b.since ?? Infinity));
  return <section aria-labelledby="needs-attention" className="border border-paper/25 px-7 py-10 sm:px-12 sm:py-12" data-id="element-246" data-mp="1k2ktzbkahn4w">
      <h2 id="needs-attention" className="label m-0 text-paper/40" data-id="element-247" data-mp="1p02wok7pr64g">
        Needs Attention
      </h2>

      {rows.length === 0 ? <p className="mt-7 font-display text-3xl leading-tight sm:text-4xl" data-id="element-248" data-mp="1b7ws7vdxanku">Nothing waiting.</p> : <>
          <p className="mt-7 flex items-baseline gap-5" data-id="element-249" data-mp="11btj25knlooe">
            <span className="font-display text-6xl leading-none sm:text-7xl" data-id="element-250" data-mp="11a8nqdqim1ph">{rows.length}</span>
            <span className="font-display text-xl leading-tight text-paper/60" data-id="element-251" data-mp="16chfj6rnixq0">
              {rows.length === 1 ? 'item needs your attention' : 'items need your attention'}
            </span>
          </p>

          <ol className="m-0 mt-9 list-none border-t border-ruleDark p-0" data-id="element-252" data-mp="wd0ej2nch80d">
            {rows.map(row => <li key={row.key} data-id="element-253" data-mp="2mn1w32psis61">
                <button type="button" onClick={() => openActivity({
            id: row.key,
            projectId: row.projectId,
            projectName: '',
            actorName: '',
            action: '',
            deliverableId: row.deliverableId,
            at: Date.now()
          })} className="flex w-full flex-wrap items-baseline justify-between gap-x-8 gap-y-2 border-b border-ruleDark py-5 text-left transition-opacity duration-150 ease-out hover:opacity-70" data-id="element-254" data-mp="2qr9opazdcs9j">
                  <span className="min-w-0" data-id="element-255" data-mp="1e8oiw2mq1djg">
                    <span className="block font-display text-xl leading-none sm:text-2xl" data-id="element-256" data-mp="19amxvc5qj45c">
                      {row.headline}
                    </span>
                    <span className="label mt-2.5 block text-paper/40" data-id="element-257" data-mp="14aik3tw7onit">{row.detail}</span>
                    <span className="label-sm mt-1.5 block text-paper/30" data-id="element-258" data-mp="zfcwmqn5v0sm">{row.next}</span>
                  </span>
                  {waitingLabel(row.since) && <span className="label-sm shrink-0 text-paper/40" data-id="element-259" data-mp="36y8pe1xfi9km">
                      {waitingLabel(row.since)}
                    </span>}
                </button>
              </li>)}
          </ol>
        </>}
    </section>;
}